import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Loading,
  ELoadingSize,
  TextStyle,
  Button,
  Icon,
} from "@rentiohq/web-shared/dist/components";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import styled from "@rentiohq/web-theme";
import React, { useState, useRef } from "react";
import { RM_COLORS } from "../rentmatch.colors";
import { PropertyNativeCalendar } from "./PropertyNativeCalendar";

interface IPropertyAvailabilityConfigProps {
  propertyId: number;
  propertyAddress?: string;
  onClose: () => void;
  defaultBrokerUserId?: number;
  availabilitySlotCount?: number | null;
  isLoadingSlots?: boolean;
  onRemainingCountChange?: (remaining: number) => void;
  hasTenant?: boolean;
  noWrapper?: boolean;
}

const InlinePanel = styled.div`
  background: white;
  border-radius: 4px;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow:
    0 0 0 1px rgba(63, 63, 68, 0.1),
    0 1px 3px 0 rgba(63, 63, 68, 0.15);
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${RM_COLORS.gray100};
  background: ${RM_COLORS.gray50};
`;

const PanelContent = styled.div`
  padding: 24px;
  min-height: 400px;
  position: relative;
`;

export const PropertyAvailabilityConfig: React.FC<
  IPropertyAvailabilityConfigProps
> = ({
  propertyId,
  propertyAddress,
  onClose,
  defaultBrokerUserId,
  availabilitySlotCount,
  isLoadingSlots,
  onRemainingCountChange: onRemainingCountChangeProp,
  hasTenant,
  noWrapper,
}) => {
  const [isLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [remainingSlots, setRemainingSlots] = useState<number | null>(null);
  const periodsRef = useRef<any[]>([]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await api.post(`/cronofy/properties/${propertyId}/availability`, {
        data: { availablePeriods: periodsRef.current },
        isV2: true,
      });
      toast({
        heading: getLocalizedText("rentmatch.calendar_saved"),
        variation: "success",
      });
      onClose();
    } catch (e) {
      console.error("Failed saving periods", e); // eslint-disable-line no-console
      toast({
        heading: getLocalizedText("rentmatch.calendar_save_error"),
        variation: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const Wrapper = noWrapper ? React.Fragment : InlinePanel;

  return (
    <Wrapper>
      <PanelHeader>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "24px",
              height: "24px",
              flexShrink: 0,
              color:
                (availabilitySlotCount ?? 0) > 0
                  ? RM_COLORS.green
                  : RM_COLORS.amber,
            }}
          >
            <Icon source="calendar" size="small" />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <TextStyle variation="strong" size="medium">
              {getLocalizedText(
                "property.applications.availability.config.label",
              )}
            </TextStyle>
            {isLoadingSlots && (
              <TextStyle variation="subdued" size="small">
                {getLocalizedText("system.loading")}
              </TextStyle>
            )}
            {!isLoadingSlots &&
              (availabilitySlotCount ?? 0) > 0 &&
              remainingSlots !== null && (
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color:
                      remainingSlots > 0 ? RM_COLORS.green : RM_COLORS.amber,
                    lineHeight: 1.5,
                  }}
                >
                  {remainingSlots > 0
                    ? getLocalizedText(
                        "property.applications.availability.slots_left",
                        {
                          count: String(remainingSlots),
                          s: remainingSlots !== 1 ? "s" : "",
                        },
                      )
                    : getLocalizedText(
                        "property.applications.availability.slots_full",
                      )}
                </span>
              )}
            {!isLoadingSlots &&
              (availabilitySlotCount ?? 0) > 0 &&
              remainingSlots === null && (
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: RM_COLORS.green,
                    lineHeight: 1.5,
                  }}
                >
                  {getLocalizedText(
                    "property.applications.availability.configured",
                    {
                      count: String(availabilitySlotCount),
                      s: availabilitySlotCount !== 1 ? "s" : "",
                    },
                  )}
                </span>
              )}
            {!isLoadingSlots && (availabilitySlotCount ?? 0) <= 0 && (
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: RM_COLORS.amber,
                  lineHeight: 1.5,
                }}
              >
                {getLocalizedText(
                  "property.applications.availability.not_configured",
                )}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <Button appearance="ghost" size="small" onClick={onClose}>
            {getLocalizedText("system.cancel")}
          </Button>
          {!hasTenant && (
            <Button
              appearance="primary"
              size="small"
              onClick={handleSave}
              isDisabled={isSaving || !hasChanges}
            >
              {getLocalizedText("system.save")}
            </Button>
          )}
        </div>
      </PanelHeader>

      <PanelContent>
        {hasTenant && (
          <div style={{ padding: "40px 0", textAlign: "center" }}>
            <TextStyle variation="subdued">
              {getLocalizedText(
                "property.applications.availability.tenant_active",
              )}
            </TextStyle>
          </div>
        )}
        {isLoading && !hasTenant && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px",
            }}
          >
            <Loading size={ELoadingSize.Large} />
          </div>
        )}
        {error && !isLoading && !hasTenant && (
          <TextStyle variation="negative">{error}</TextStyle>
        )}
        {!isLoading && !error && !hasTenant && (
          <PropertyNativeCalendar
            propertyId={propertyId}
            propertyAddress={propertyAddress}
            onUpdateSlots={slots => {
              periodsRef.current = slots;
            }}
            onDirty={() => setHasChanges(true)}
            defaultBrokerUserId={defaultBrokerUserId}
            onRemainingCountChange={count => {
              setRemainingSlots(count);
              onRemainingCountChangeProp?.(count);
            }}
          />
        )}
      </PanelContent>
    </Wrapper>
  );
};
